export type Locale = 'pt' | 'es' | 'en';

type LocalizedBag = Record<string, Record<string, unknown> | undefined> | null | undefined;

export function pickLocalizedValue<T = string>(
  translations: LocalizedBag,
  locale: Locale,
  key: string,
  fallback: T | '' = '' as T,
): T | '' {
  const localized = translations?.[locale]?.[key];
  if (localized !== undefined && localized !== null && localized !== '') return localized as T;

  const pt = translations?.pt?.[key];
  if (pt !== undefined && pt !== null && pt !== '') return pt as T;

  return fallback;
}

function pickLocalizedObject<T>(
  value: Record<string, T | undefined> | null | undefined,
  locale: Locale,
  fallback: T,
): T {
  return value?.[locale] || value?.pt || fallback;
}

// Data efetiva de publicacao de uma linha: usa published_at quando existir
// (auto-preenchido ao publicar, requisito 6); cai para release_date em
// registros legados que nunca passaram pelo fluxo novo de auto-stamp.
function effectivePublishDate(row: { published_at?: string | null; release_date?: string | null }): string {
  return String(row.published_at || row.release_date || '');
}

/**
 * Ordena por destaque primeiro (featured=true no topo) e, dentro de cada grupo,
 * pela data efetiva de publicacao mais recente primeiro (requisito 11).
 * Usado pela listagem geral/publica da Sala de Imprensa - NAO usar para achar
 * o "ultimo press release" (ver sortPressRowsByRecency).
 */
export function sortPressRows<T extends { release_date?: string | null; published_at?: string | null; featured?: boolean | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return effectivePublishDate(b).localeCompare(effectivePublishDate(a));
  });
}

/**
 * Ordena apenas pela data efetiva de publicacao mais recente, IGNORANDO
 * featured (requisito 3). Usada para determinar o "ultimo press release"
 * exibido no Footer e na Sala de Imprensa - o destaque nao deve interferir
 * nessa determinacao, so a ordenacao geral (requisito 11) usa featured.
 */
export function sortPressRowsByRecency<T extends { release_date?: string | null; published_at?: string | null; status?: string | null }>(rows: T[]): T[] {
  return [...rows]
    .filter((row) => !row.status || row.status === 'published')
    .sort((a, b) => effectivePublishDate(b).localeCompare(effectivePublishDate(a)));
}

export function mapPressReleaseRow(row: any, locale: Locale) {
  return {
    id: row.slug,
    slug: row.slug,
    title: pickLocalizedValue(row.translations, locale, 'title'),
    date: row.release_date || '',
    published_at: row.published_at || null,
    status: row.status || 'published',
    category: row.category || 'Release',
    excerpt: pickLocalizedValue(row.translations, locale, 'excerpt'),
    content: pickLocalizedValue(row.translations, locale, 'body'),
    featured: Boolean(row.featured),
    image: row.cover_image || '',
    author: row.author || 'TechEnabler',
    seo_title: pickLocalizedValue(row.translations, locale, 'seo_title'),
    seo_description: pickLocalizedValue(row.translations, locale, 'seo_description'),
  };
}

export function mapSolutionRow(row: any, locale: Locale) {
  const technical = pickLocalizedObject<Record<string, unknown>>(row.technical, locale, {});

  return {
    title: pickLocalizedValue(row.translations, locale, 'title'),
    slug: row.slug,
    sku: row.sku || 'TE-DATASHEET',
    subtitle: pickLocalizedValue(row.translations, locale, 'subtitle'),
    tagline: pickLocalizedValue(row.translations, locale, 'tagline', null as any),
    description: pickLocalizedValue(row.translations, locale, 'short_description'),
    seo_title: pickLocalizedValue(row.translations, locale, 'seo_title'),
    seo_description: pickLocalizedValue(row.translations, locale, 'seo_description'),
    image: row.cover_image || '',
    body: pickLocalizedValue(row.translations, locale, 'body'),
    primaryMarket: row.market_id || '',
    marketId: row.market_id || '',
    scope: row.scope || '',
    category: row.category_label || '',
    targetAudience: pickLocalizedObject(row.target_audience, locale, {}),
    benefits: pickLocalizedObject(row.benefits, locale, []),
    keyFeatures: technical.keyFeatures || [],
    technicalDetails: technical.technicalDetails || [],
    competitiveAdvantages: technical.competitiveAdvantages || [],
    uniqueValue: technical.uniqueValue || '',
    technicalCategories: technical.technicalCategories || [],
    relatedMarkets: technical.relatedMarkets || [],
    commercialModels: row.commercial_models || [],
  };
}

export function mapMarketRow(row: any, locale: Locale) {
  return {
    id: row.id,
    title: pickLocalizedValue(row.translations, locale, 'title'),
    desc: pickLocalizedValue(row.translations, locale, 'description'),
    icon: row.icon || 'Network',
    sort_order: row.sort_order || 0,
  };
}

export function mapCategoryRow(row: any, locale: Locale) {
  return {
    id: row.id,
    market_id: row.market_id,
    scope: row.scope,
    slug: row.slug,
    title: pickLocalizedValue(row.translations, locale, 'title'),
    sort_order: row.sort_order || 0,
  };
}
